import { getScrollContainer, getScrollEventTarget, scrollContainerMediaQuery } from '@theme/scroll-container';

class PremiumHeader extends HTMLElement {
  connectedCallback() {
    this.openButton = this.querySelector('[data-pch-open]');
    this.closeButton = this.querySelector('[data-pch-close]');
    this.drawer = this.querySelector('[data-pch-drawer]');
    this.headerGroup = this.closest('#header-group');
    this.resizeObserver = new ResizeObserver(this.measureHeights);
    this.resizeObserver.observe(this);
    for (const section of this.headerGroup?.children || []) this.resizeObserver.observe(section);
    this.measureHeights();
    this.lastScrollY = this.scrollPosition();
    this.scrollDistance = 0;
    this.scrollDirection = 0;
    this.setHidden(false);
    this.bindScroll();
    scrollContainerMediaQuery.addEventListener('change', this.bindScroll);
    document.addEventListener('keydown', this.handlePageKeydown);
    this.addEventListener('focusin', this.reveal);

    if (!this.openButton || !this.closeButton || !this.drawer) return;
    this.openButton.addEventListener('click', this.open);
    this.closeButton.addEventListener('click', this.close);
    this.drawer.addEventListener('click', this.handleDrawerClick);
    this.drawer.addEventListener('cancel', this.handleCancel);
    this.drawer.addEventListener('close', this.restorePage);
    this.drawer.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize, { passive: true });
  }

  disconnectedCallback() {
    this.close();
    this.resizeObserver?.disconnect();
    this.openButton?.removeEventListener('click', this.open);
    this.closeButton?.removeEventListener('click', this.close);
    this.drawer?.removeEventListener('click', this.handleDrawerClick);
    this.drawer?.removeEventListener('cancel', this.handleCancel);
    this.drawer?.removeEventListener('close', this.restorePage);
    this.drawer?.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('resize', this.handleResize);
    this.scrollTarget?.removeEventListener('scroll', this.handleScroll);
    scrollContainerMediaQuery.removeEventListener('change', this.bindScroll);
    document.removeEventListener('keydown', this.handlePageKeydown);
    this.removeEventListener('focusin', this.reveal);
    cancelAnimationFrame(this.scrollFrame);
    this.setHidden(false);
    document.body.style.setProperty('--sticky-header-offset', '0px');
  }

  measureHeights = () => {
    const height = this.offsetHeight;
    const groupHeight = [...(this.headerGroup?.children || [])].reduce((total, section) => total + section.offsetHeight, 0);
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--header-group-height', `${groupHeight}px`);
    document.body.style.setProperty('--sticky-header-offset', this.dataset.sticky === 'true' && this.dataset.scrollHidden !== 'true' ? `${height}px` : '0px');
  };

  bindScroll = () => {
    this.scrollTarget?.removeEventListener('scroll', this.handleScroll);
    this.scrollTarget = getScrollEventTarget();
    this.lastScrollY = this.scrollPosition();
    this.scrollDistance = 0;
    this.scrollDirection = 0;
    this.reveal();
    this.scrollTarget.addEventListener('scroll', this.handleScroll, { passive: true });
  };

  scrollPosition = () => {
    const container = getScrollContainer();
    return Math.max(0, Math.min(container.scrollTop, container.scrollHeight - container.clientHeight));
  };

  handleScroll = () => {
    if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(this.updateScroll);
  };

  updateScroll = () => {
    this.scrollFrame = null;
    const position = this.scrollPosition();
    const delta = position - this.lastScrollY;
    this.lastScrollY = position;
    if (this.dataset.sticky !== 'true' || position <= 8 || this.drawer?.open
      || this.querySelector('dialog[open], :focus-visible') || document.documentElement.hasAttribute('scroll-lock')) {
      this.scrollDistance = 0;
      this.setHidden(false);
      return;
    }
    if (!delta) return;
    const direction = Math.sign(delta);
    this.scrollDistance = direction === this.scrollDirection ? this.scrollDistance + Math.abs(delta) : Math.abs(delta);
    this.scrollDirection = direction;
    if (this.scrollDistance < 10) return;
    // Keep navigation available when returning up the page.
    this.setHidden(direction > 0);
    this.scrollDistance = 0;
  };

  setHidden(hidden) {
    this.dataset.scrollHidden = String(hidden);
    this.inert = hidden;
    document.body.style.setProperty('--sticky-header-offset', this.dataset.sticky === 'true' && !hidden ? `${this.offsetHeight}px` : '0px');
  }

  reveal = () => this.setHidden(false);

  handlePageKeydown = (event) => {
    if (event.key === 'Tab') this.reveal();
  };

  open = () => {
    if (this.drawer.open) return;
    this.reveal();
    this.previouslyFocused = document.activeElement;
    this.hadScrollLock = document.documentElement.hasAttribute('scroll-lock');
    // The native modal provides background isolation and traps focus, including closed details.
    this.dataset.open = 'true';
    this.drawer.showModal();
    this.openButton.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
    this.closeButton.focus({ preventScroll: true });
  };

  close = () => {
    if (!this.drawer?.open) return;
    this.drawer.close();
    this.restorePage();
  };

  restorePage = () => {
    if (this.dataset.open !== 'true') return;
    this.dataset.open = 'false';
    this.openButton.setAttribute('aria-expanded', 'false');
    if (!this.hadScrollLock) document.documentElement.removeAttribute('scroll-lock');
    this.previouslyFocused?.focus?.({ preventScroll: true });
  };

  handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  handleDrawerClick = (event) => {
    if (event.target.closest('a')) this.close();
    // Backdrop clicks target the dialog; blank space inside the panel should stay open.
    if (event.target === this.drawer) {
      const rect = this.drawer.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
    }
  };

  handleKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')]
      .filter((element) => element.getClientRects().length && getComputedStyle(element).visibility === 'visible');
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  };

  handleResize = () => {
    if (window.matchMedia('(min-width: 990px)').matches) this.close();
  };
}

if (!customElements.get('premium-header')) {
  customElements.define('premium-header', PremiumHeader);
}
